import { TabSelector, useFormState, CustomButton } from 'holistic-resources';
import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Button from 'components/Button';
import SpeakToYourAgentCardShort from 'components/SpeakToYourAgentCardShort';
import DefaultHeader from 'components/pageLayout/DefaultHeader';
import Layout from 'components/pageLayout/Layout';
import PageContentContainer from 'components/pageLayout/PageContentContainer';
import SectionLabel from 'components/typography/SectionLabel';
import useWhiteLabelSettings from 'hooks/useWhiteLabelSettings';
import { getMe } from 'services/ApiService';
import { getClientApis } from 'services/ClientService';
import { getSavedGroupTags } from 'services/CommonService';
import { BUTTON_TYPE_SUBMIT } from 'shared/constants/common';
import { generateDropdownOptionsFromTagManagerValue } from 'shared/helpers/common';
import { ReactComponent as ArrowLeft } from '../../images/icons/arrowleft.svg';
import { ReactComponent as ArrowRight } from '../../images/icons/arrownext.svg';
import PassportItem from './PassportItem';
import PassportModal from './PassportModal';
import { tabOptions } from './constants';

export default function DocumentPage() {
  const { whiteLabelSettings: { secondaryColour } = {} } =
    useWhiteLabelSettings();
  const [selectedTab, setSelectedTab] = useState(tabOptions[0]);
  const selectedTabValue = selectedTab ? selectedTab.value : null;
  const [passports, setPassports] = useState([]);
  const [formDropdownConfig, setFormDropdownConfig] = useState({
    countryOfResidence: [],
    nationality: [],
  });
  const [visitor, setVisitor] = useState({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { setFormValue } = useFormState({});
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1025);
  const [showTabMenu, setShowTabMenu] = useState(true);
  const { componentConfig } = useSelector((state) => state.ui);

  const itemClass =
    'mt-[20px] py-2 font-medium text-[22px] flex items-center justify-between';

  const loadData = useCallback(async () => {
    const APIS_TAG = 'APIS';
    try {
      const { data: { results = [] } = {} } = await getClientApis();
      const { data: { APIS: apisData = [] } = {} } = await getSavedGroupTags([
        APIS_TAG,
      ]);
      const { data: user } = await getMe();
      setFormDropdownConfig(
        generateDropdownOptionsFromTagManagerValue(apisData),
      );
      setPassports(results);
      setVisitor(user);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const isMobile = window.innerWidth < 1025;
    setIsMobileView(isMobile);
    setShowTabMenu(true);
  }, []);

  const updatePassportList = (id, updatedData = {}) => {
    const tempPassports = passports || [];
    const updatedPassportList = tempPassports.map((passport) => {
      if (passport.id === id) {
        return { ...updatedData, id };
      }

      return { ...passport };
    });

    setPassports(updatedPassportList);
  };

  const onPassportUpdate = (id, updatedData) => {
    updatePassportList(id, updatedData);
  };

  const removeDeletedPassport = (deletedPassportId) => {
    const tempPassports = passports || [];
    const filteredPassports = tempPassports.filter(
      ({ id }) => id !== deletedPassportId,
    );

    setPassports(filteredPassports);
  };

  const onPassportDelete = (deletedPassportId) => {
    removeDeletedPassport(deletedPassportId);
  };

  const addNewPassport = () => {
    setIsAddModalOpen(true);
    setFormValue({});
  };

  return (
    <>
      <Layout>
        <DefaultHeader label="My Travel Documents" />
        <PageContentContainer>
          <div className="flex mt-[40px] w-full">
            <div
              className={`flex flex-col border-r ml-8 w-full lg:w-[25%] ${
                isMobileView && !showTabMenu ? 'hidden' : ''
              }`}
            >
              <SectionLabel label="Documents" />
              <TabSelector
                tabList={tabOptions.map((tab) => ({
                  ...tab,
                  label: (
                    <div className="flex items-center">
                      <span>{tab.label}</span>
                      {isMobileView && <ArrowRight className="w-5 ml-4" />}
                    </div>
                  ),
                }))}
                selectedTabValue={selectedTabValue}
                onTabSelect={(tab) => {
                  setSelectedTab(tab);
                  if (isMobileView) setShowTabMenu(false);
                }}
                tabClassName="grow font-medium text-center mt-[12px] text-[22px]"
                containerClassName="flex flex-col mt-[28px]"
                selectedTabStyle={{ color: secondaryColour }}
                highlightBarStyle={{
                  backgroundColor: secondaryColour,
                  borderRadius: '99px',
                  padding: '2px',
                }}
                tabContentStyle={{
                  display: 'flex',
                  flexDirection: 'row-reverse',
                  justifyContent: 'flex-end',
                }}
                labelStyle={{ padding: '10px 15px' }}
              />
              {componentConfig.contactAgentEnabled && (
                <div className="mr-[40px] mb-[40px]">
                  <SpeakToYourAgentCardShort />
                </div>
              )}
            </div>
            <div className="grow">
              <div
                className={`ml-[20px] mr-[15px] w-full lg:w-[75%] ${
                  isMobileView && showTabMenu ? 'hidden' : 'block'
                }`}
              >
                {isMobileView && (
                  <CustomButton
                    className={`${itemClass} mt-[0px]`}
                    onClick={() => setShowTabMenu(true)}
                  >
                    <ArrowLeft className="w-5 mr-4" />
                    Back
                  </CustomButton>
                )}
                <SectionLabel label="Passports" className="lg:mb-[40px]" />
                <div className="flex flex-wrap gap-[40px] p-[15px] ml-[-15px] pl-[0px] lg:pl-[15px] lg:p-0">
                  {passports.map((passport, index) => (
                    <PassportItem
                      key={passport?.id || index}
                      data={passport}
                      onPassportUpdate={onPassportUpdate}
                      onPassportDelete={onPassportDelete}
                      formDropdownConfig={formDropdownConfig}
                    />
                  ))}
                  <div className="flex justify-end w-full self-end basis-full">
                    <Button
                      type={BUTTON_TYPE_SUBMIT}
                      onClick={addNewPassport}
                      className="mb-4 mr-4"
                    >
                      Add a passport
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PageContentContainer>
      </Layout>
      <PassportModal
        isOpen={isAddModalOpen}
        setModalState={setIsAddModalOpen}
        onPassportUpdate={onPassportUpdate}
        formDropdownConfig={formDropdownConfig}
        isNewPassport
        visitor={visitor}
        passportType="client"
      />
    </>
  );
}
